import React, { useState } from 'react';
import styled from 'styled-components';

import SectionContainer from '../components/SectionContainer';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';
import { submitContactForm } from '../services/contactService';

// Contact Hero
const ContactHero = styled.div`
  padding-top: 150px;
  padding-bottom: var(--spacing-lg);
  @media (max-width: 768px) {
    padding-top: 120px;
  }
`;

// Contact Layout
const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-lg);
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

// Contact Form
const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: var(--spacing-xs);
  font-size: 0.85rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-medium-gray);
  background-color: transparent;
  font-family: var(--font-body);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--color-text);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-medium-gray);
  background-color: transparent;
  font-family: var(--font-body);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--color-text);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-medium-gray);
  background-color: transparent;
  font-family: var(--font-body);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--color-text);
  }
`;

const SubmitButton = styled(Button)`
  align-self: flex-start;
`;

// Contact Info
const ContactInfo = styled.div`
  background-color: var(--color-light-gray);
  padding: var(--spacing-lg);
`;

const ContactInfoTitle = styled.h3`
  margin-bottom: var(--spacing-md);
`;

const ContactInfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContactInfoItem = styled.li`
  display: flex;
  margin-bottom: var(--spacing-md);
`;

const ContactIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: var(--color-light-gray);
  border-radius: 50%;
  margin-right: var(--spacing-md);
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ContactText = styled.div``;

const ContactLabel = styled.div`
  font-weight: 500;
  margin-bottom: var(--spacing-xs);
`;

const ContactDetail = styled.div`
  font-size: 0.85rem;
  line-height: 1.6;
`;

// Map
const Map = styled.div`
  height: 300px;
  background-color: var(--color-medium-gray);
  margin-top: var(--spacing-lg);
  position: relative;
  filter: grayscale(100%);
`;

const MapPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-md);
`;

// Success Message
const SuccessMessage = styled.div`
  background-color: var(--color-light-gray);
  padding: var(--spacing-lg);
  text-align: center;
`;

const SuccessTitle = styled.h3`
  margin-bottom: var(--spacing-md);
`;

const SuccessText = styled.p`
  margin-bottom: var(--spacing-md);
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

// Petite image sur colonne droite
const ContactImage = styled.img`
  width: 100%;
  max-width: 320px;
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
`;

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        serviceType: formData.service
      });
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError("Une erreur s'est produite lors de l'envoi du formulaire. Veuillez réessayer.");
      }
    } catch (err) {
      setError("Une erreur s'est produite lors de l'envoi du formulaire. Veuillez réessayer.");
      console.error("Erreur lors de la soumission:", err);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      <SectionContainer background="white" spacing="small">
        <ContactHero>
          <PageHeader 
            title="Contact" 
            subtitle="Prenez rendez-vous pour discuter de votre projet." 
          />
        </ContactHero>
      </SectionContainer>
      
      <SectionContainer background="light-gray">
        {!isSubmitted ? (
          <ContactLayout>
            <ContactForm onSubmit={handleSubmit}>
              {error && (
                <div style={{ color: 'red', marginBottom: 'var(--spacing-md)' }}>
                  {error}
                </div>
              )}
              
              <FormGroup>
                <FormLabel htmlFor="name">Nom complet</FormLabel>
                <FormInput 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="phone">Téléphone</FormLabel>
                <FormInput 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="service">Service qui vous intéresse</FormLabel>
                <FormSelect 
                  id="service" 
                  name="service" 
                  value={formData.service} 
                  onChange={handleChange} 
                  required
                >
                  <option value="">Sélectionnez un service</option>
                  <option value="Architecture d'intérieur">Architecture d'intérieur</option>
                  <option value="Décoration d'intérieur">Décoration d'intérieur</option>
                  <option value="Conception sur mesure">Conception sur mesure</option>
                  <option value="Décors éphémères">Décors éphémères</option>
                  <option value="Conseil en aménagement">Conseil en aménagement</option>
                  <option value="Clients particuliers">Clients particuliers</option>
                  <option value="B2B">B2B</option>
                </FormSelect>
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">Votre message</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  required 
                />
              </FormGroup>
              
              <SubmitButton>
                {isLoading ? 'Envoi en cours...' : 'Envoyer'}
              </SubmitButton>
            </ContactForm>
            
            <ContactInfo>
              <ContactImage src="/images/contact.png" alt="Contact" />
              <ContactInfoTitle>Informations de contact</ContactInfoTitle>
              
              <ContactInfoList>
                <ContactInfoItem>
                  <ContactIcon>
                    <svg viewBox="0 0 384 512" fill="currentColor">
                      <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"></path>
                    </svg>
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Adresse</ContactLabel>
                    <ContactDetail>23 rue Octave Feuillet</ContactDetail>
                    <ContactDetail>75116 Paris</ContactDetail>
                  </ContactText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactIcon>
                    <svg viewBox="0 0 512 512" fill="currentColor">
                      <path d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"></path>
                    </svg>
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Email</ContactLabel>
                    <ContactDetail>fabienne@agence-interieurs.fr</ContactDetail>
                  </ContactText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactIcon>
                    <svg viewBox="0 0 512 512" fill="currentColor">
                      <path d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"></path>
                    </svg>
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Téléphone</ContactLabel>
                    <ContactDetail>Tél. : +33.(0)6.60.73.24.01</ContactDetail>
                  </ContactText>
                </ContactInfoItem>
                
                <ContactInfoItem>
                  <ContactIcon>
                    <svg viewBox="0 0 512 512" fill="currentColor">
                      <path d="M256,8C119,8,8,119,8,256S119,504,256,504,504,393,504,256,393,8,256,8Zm92.49,313h0l-20,25a16,16,0,0,1-22.49,2.5h0l-67-49.72a40,40,0,0,1-15-31.23V112a16,16,0,0,1,16-16h32a16,16,0,0,1,16,16V256l58,42.5A16,16,0,0,1,348.49,321Z"></path>
                    </svg>
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>Horaires</ContactLabel>
                    <ContactDetail>Lundi - Vendredi: 9h - 18h</ContactDetail>
                    <ContactDetail>Samedi: Sur rendez-vous</ContactDetail>
                    <ContactDetail>Dimanche: Fermé</ContactDetail>
                  </ContactText>
                </ContactInfoItem>
              </ContactInfoList>
              
              <Map>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.81447952799!2d2.269075576571338!3d48.86174797133256!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66f74095b975f%3A0xf07176476d3cbe42!2sAgence%20Int%C3%A9rieurs%20%7C%20Paris%20%7C%20Architecte%20d'int%C3%A9rieur%20UFDI%2075!5e0!3m2!1sfr!2ses!4v1749643448569!5m2!1sfr!2ses"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </Map>
            </ContactInfo>
          </ContactLayout>
        ) : (
          <SuccessMessage>
            <SuccessTitle>Merci pour votre message !</SuccessTitle>
            <SuccessText>
              Votre demande de contact a bien été envoyée. Notre équipe vous contactera dans les plus brefs délais, généralement sous 24 à 48 heures ouvrables.
            </SuccessText>
            <Button onClick={() => setIsSubmitted(false)}>Retour au formulaire</Button>
          </SuccessMessage>
        )}
      </SectionContainer>
    </>
  );
};

export default ContactPage;
